/**
 * Фото накладной: страницы, масштаб, поворот с сохранением, перетаскивание.
 *
 * - **Вписано по умолчанию.** Страница целиком помещается в область; «+»
 *   увеличивает относительно этого размера.
 * - **Листать при увеличении** — перетаскиванием мышью (на телефоне —
 *   пальцем, обычной прокруткой) и колесом. Ctrl + колесо — масштаб.
 *   Увеличенное фото прокручивается во все стороны: центр держит
 *   `margin: auto`, а не `justify-content: center`, который отрезал бы
 *   левый край (жалоба владельца 16.09.2026).
 * - **Поворот** сначала только на экране; «Сохранить поворот» поворачивает
 *   сам файл на сервере, и страница открывается правильно у всех. Агент
 *   после этого перечитывает накладную.
 * - **Рамка — по самому фото**, а не по всей колонке (владелец, 30.09.2026).
 *   Окно, в котором фото листается, имеет размер фото и растёт вместе с
 *   увеличением, пока не упрётся в свободное место; дальше — прокрутка
 *   внутри него. Раньше окном была вся колонка: полосы прокрутки уезжали к
 *   краям экрана, далеко от фото, а номера страниц — под нижний край.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { photoObjectUrl, portalApi } from '../../api';
import type { ReceiptDetail } from '../../api';
import { Icon } from '../../icons';

/** Масштаб относительно «вписано в область». */
const ZOOMS = [1, 1.5, 2, 3, 4];

/** Поля вокруг рамки и место под номера страниц — вычитаются из свободного места. */
const GUTTER_X = 34;
const GUTTER_Y = 20;
const THUMBS_HEIGHT = 58;
/** Тонкая полоса прокрутки (`scrollbar-width: thin`): рамке нужно место под неё. */
const SCROLLBAR = 12;

/**
 * Размер рамки: как у фото, но не больше свободного места. Где фото шире
 * или выше рамки, появляется полоса прокрутки, и ей добавляется место —
 * иначе полоса одной оси съедала бы край другой.
 */
export function frameSize(
  box: { width: number; height: number },
  room: { width: number; height: number },
): { width: number; height: number } {
  const overX = box.width > room.width + 0.5;
  const overY = box.height > room.height + 0.5;
  return {
    width: Math.min(box.width + (overY ? SCROLLBAR : 0), room.width),
    height: Math.min(box.height + (overX ? SCROLLBAR : 0), room.height),
  };
}

function usePhoto(url: string | undefined) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setFailed(false);
    setSrc(null);
    photoObjectUrl(url)
      .then((value) => {
        if (value.startsWith('blob:')) objectUrl = value;
        if (!cancelled) setSrc(value);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  return { src, failed };
}

function useSize(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

export function PhotoViewer({
  receiptId,
  pages,
}: {
  receiptId: number;
  pages: ReceiptDetail['pages'];
}) {
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(0);
  const [turn, setTurn] = useState(0);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Свободное место под фото — и рамка внутри него, в которой фото листается.
  const roomRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const space = useSize(roomRef);
  const drag = useRef<{ x: number; y: number; left: number; top: number; id: number } | null>(
    null,
  );

  const page = pages[Math.min(index, pages.length - 1)];
  const { src, failed } = usePhoto(page?.url);

  const rotate = useMutation<{ message: string }, ApiError, number>({
    mutationFn: (degrees) => portalApi.rotatePhoto(receiptId, page.photo_id, degrees),
    onSuccess: (result) => {
      setTurn(0);
      setNotice(result.message);
      // Ключ `['receipts']` накрывает и карточку накладной: новый адрес
      // фото приходит с сервера вместе с ней.
      void queryClient.invalidateQueries({ queryKey: ['receipts'] });
    },
    onError: (error) => setNotice(error.detail ?? 'Поворот не сохранился. Попробуйте ещё раз.'),
  });

  useEffect(() => {
    setNatural(null);
  }, [src]);

  if (pages.length === 0) {
    return (
      <div className="viewer viewer--empty">
        <p>Фото к этой накладной не сдавали.</p>
      </div>
    );
  }

  const quarter = turn % 180 !== 0;
  const boxWidth = natural ? (quarter ? natural.height : natural.width) : 0;
  const boxHeight = natural ? (quarter ? natural.width : natural.height) : 0;
  const room = {
    width: Math.max(space.width - GUTTER_X, 50),
    height: Math.max(space.height - GUTTER_Y - (pages.length > 1 ? THUMBS_HEIGHT : 0), 50),
  };
  const fit = natural ? Math.min(room.width / boxWidth, room.height / boxHeight) : 1;
  const scale = fit * ZOOMS[zoom];
  // Целые пиксели: на дробном размере рамка получала полосы прокрутки и
  // там, где фото помещается целиком.
  const box = { width: Math.floor(boxWidth * scale), height: Math.floor(boxHeight * scale) };
  const frame = frameSize(box, room);

  const changeZoom = (next: number) => setZoom(Math.max(0, Math.min(ZOOMS.length - 1, next)));

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !stageRef.current) return;
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      left: stageRef.current.scrollLeft,
      top: stageRef.current.scrollTop,
      id: event.pointerId,
    };
    stageRef.current.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    if (!start || !stageRef.current) return;
    stageRef.current.scrollLeft = start.left - (event.clientX - start.x);
    stageRef.current.scrollTop = start.top - (event.clientY - start.y);
  };
  const endDrag = () => {
    drag.current = null;
  };
  const onWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (!event.ctrlKey) return;
    event.preventDefault();
    changeZoom(zoom + (event.deltaY < 0 ? 1 : -1));
  };

  return (
    <div className="viewer">
      <div className="viewer__toolbar">
        <span className="viewer__page">
          Страница {index + 1} из {pages.length}
        </span>
        <span className="spacer" />
        <button
          type="button"
          className="icon-button"
          onClick={() => changeZoom(zoom - 1)}
          disabled={zoom === 0}
          aria-label="Уменьшить"
        >
          <Icon name="minus" size={16} />
        </button>
        <span className="viewer__zoom">
          {zoom === 0 ? 'по размеру' : `${Math.round(ZOOMS[zoom] * 100)}%`}
        </span>
        <button
          type="button"
          className="icon-button"
          onClick={() => changeZoom(zoom + 1)}
          disabled={zoom === ZOOMS.length - 1}
          aria-label="Увеличить"
        >
          <Icon name="plus" size={16} />
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={() => {
            setTurn((t) => (t + 90) % 360);
            setNotice(null);
          }}
          aria-label="Повернуть по часовой стрелке"
          title="Повернуть"
        >
          <Icon name="rotate" size={16} />
        </button>
      </div>
      {(turn !== 0 || notice) && (
        <div className="viewer__save">
          {turn !== 0 ? (
            <>
              <span>Повёрнуто на {turn}°</span>
              <span className="spacer" />
              <button
                type="button"
                className="button button--small"
                onClick={() => setTurn(0)}
                disabled={rotate.isPending}
              >
                Сбросить
              </button>
              <button
                type="button"
                className="button button--small button--primary"
                onClick={() => rotate.mutate(turn)}
                disabled={rotate.isPending}
              >
                {rotate.isPending ? 'Сохраняю…' : 'Сохранить поворот'}
              </button>
            </>
          ) : (
            <span>{notice}</span>
          )}
        </div>
      )}
      <div ref={roomRef} className="viewer__room">
        {failed ? (
          <p className="viewer__failed">Фото не загрузилось. Обновите страницу.</p>
        ) : src ? (
          <div
            ref={stageRef}
            className={`viewer__stage${zoom > 0 ? ' viewer__stage--zoomed' : ''}`}
            style={natural ? frame : { width: 1, height: 1, visibility: 'hidden' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onWheel={onWheel}
          >
            <div
              className="viewer__box"
              style={natural ? box : { width: 1, height: 1 }}
            >
              <img
                className="viewer__image"
                src={src}
                alt={`Накладная, страница ${index + 1}`}
                draggable={false}
                onLoad={(event) =>
                  setNatural({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  })
                }
                style={
                  natural
                    ? {
                        width: natural.width * scale,
                        height: natural.height * scale,
                        transform: `translate(-50%, -50%) rotate(${turn}deg)`,
                      }
                    : undefined
                }
              />
            </div>
          </div>
        ) : null}
        {pages.length > 1 && (
          <div className="viewer__thumbs">
            {pages.map((item, i) => (
              <button
                key={item.photo_id}
                type="button"
                className={`thumb${i === index ? ' thumb--active' : ''}`}
                onClick={() => {
                  setIndex(i);
                  setTurn(0);
                  setZoom(0);
                  setNotice(null);
                }}
                aria-label={`Страница ${i + 1}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
